import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { resetStandupRedirect, setSessionData } from '../../../store/slices/standupRedirectSlice';
import { checkIsSubmittedBtnClick, clearSuccessMessage } from '../../../store/slices/standUpSlice';
import { showLockExpirationWarning, hideLockExpirationWarning, showSessionLockConflictModal } from '../../../store/slices/sessionLockSlice';
import { getSessionLockService, initializeSessionLockService } from '../../../api/sessionLockService';
import { createApiClient } from '../../../api/apiClient';
import logger from '../../../utils/logger';
import { formatDateForTitle } from './useStandUpDashBoard';

interface UseStandUpScreenHandlersProps {
  setOverlayVisible: (visible: boolean) => void;
  setShowRedirectScreen: (visible: boolean) => void;
  setShowCardsScreen: (visible: boolean) => void;
  setStandupType: (type: string) => void;
  setStandupDate: (date: string) => void;
  setStandupSessionId: (id: string) => void;
  setNewsFeedOverlayVisible: (visible: boolean) => void;
  setNewsList: (callback: (prev: any[]) => any[]) => void;
  isEditMode: boolean;
  standupId: string;
  currentLockToken: string;
  setCurrentLockToken: (token: string) => void;
  currentLockResourceId: string;
  setCurrentLockResourceId: (id: string) => void;
  lockReleasedRef: React.MutableRefObject<boolean>;
  setIsEditMode: (editMode: boolean) => void;
  setEditStandupData: (data: any) => void;
  setIsViewMode: (viewMode: boolean) => void;
  setViewStandupData: (data: any) => void;
  setStandupId: (id: string) => void;
  fetchStandUpData: () => Promise<void>;
  fetchTopOpportunitiesData: (page?: number, pageSize?: number, sessionId?: string) => Promise<void>;
  fetchNewsData: () => Promise<void>;
}

export const useStandUpScreenHandlers = ({
  setOverlayVisible,
  setShowRedirectScreen,
  setShowCardsScreen,
  setStandupType,
  setStandupDate,
  setStandupSessionId,
  setNewsFeedOverlayVisible,
  setNewsList,
  isEditMode,
  standupId,
  currentLockToken,
  setCurrentLockToken,
  currentLockResourceId,
  setCurrentLockResourceId,
  lockReleasedRef,
  setIsEditMode,
  setEditStandupData,
  setIsViewMode,
  setViewStandupData,
  setStandupId,
  fetchStandUpData,
  fetchTopOpportunitiesData,
  fetchNewsData
}: UseStandUpScreenHandlersProps) => {
  const dispatch = useDispatch();
  
  // Ref to prevent multiple simultaneous edit operations
  const isEditProcessingRef = useRef(false);

  /**
   * Generate a unique session ID for new standups
   * Format: Standup_{random number}
   * @returns {string} Unique session ID
   */
  const generateStandupSessionId = useCallback((): string => {
    const sessionId = `Standup_${Math.floor(Math.random() * 1000000)}`;
    logger.log('Generated standup session ID:', sessionId);
    return sessionId;
  }, []);

  const handleOpenOverlay = useCallback(() => {
    // Clear success message when opening overlay for new standup
    dispatch(clearSuccessMessage());
    dispatch(checkIsSubmittedBtnClick(false));
    setOverlayVisible(true);
  }, [dispatch, setOverlayVisible]);

  const handleCloseOverlay = useCallback(() => {
    setOverlayVisible(false);
  }, [setOverlayVisible]);

  const handleContinue = useCallback(
    (selectedType: string, dateRange: string) => {
      logger.log('Selected:', selectedType, 'Date:', dateRange);
      // Generate a session ID for this standup
      const sessionId = generateStandupSessionId();

      // Generate standup title from type and date
      const standupTypeMap: { [key: string]: string } = {
        'weekly-lt': 'Weekly LT',
        'daily-lt': 'Daily LT',
        'daily-frontline': 'Daily Front-line',
      };
      const fullStandupType =
        standupTypeMap[selectedType.toLowerCase()] || selectedType;

      // Parse date and format title
      let standupTitle = fullStandupType;
      if (dateRange) {
        const formattedDate = formatDateForTitle(dateRange);
        standupTitle = `${fullStandupType} ${formattedDate}`;
      }

      // Store session data in Redux
      dispatch(
        setSessionData({
          sessionId,
          standupType: selectedType,
          standupDate: dateRange,
          standupTitle,
        }),
      );

      setStandupType(selectedType);
      setStandupDate(dateRange);
      setStandupSessionId(sessionId);
      setOverlayVisible(false);
      setShowRedirectScreen(true);
    },
    [dispatch, generateStandupSessionId, setStandupType, setStandupDate, setStandupSessionId, setOverlayVisible, setShowRedirectScreen],
  );

  const handleBackToStandUp = useCallback(
    async (action?: string, standupData?: any) => {
      // Release lock if we're in edit mode and have a lock token
      dispatch(checkIsSubmittedBtnClick(false));
      dispatch(resetStandupRedirect());
      
      if (isEditMode && standupId && currentLockToken && currentLockResourceId) {
        try {
          const lockService = getSessionLockService();
          if (lockService) {
            console.log('[StandUp] 🔐 Releasing lock in handleBackToStandUp for resourceId:', currentLockResourceId);
            await lockService.releaseLock(currentLockResourceId, currentLockToken);
            lockReleasedRef.current = true; // Mark as released to prevent cleanup from re-releasing
            setCurrentLockToken('');
            setCurrentLockResourceId('');
          }
        } catch (error) {
          console.error('[StandUp] Error releasing lock on back:', error);
        }
      }
      
      // Now clear the edit mode state
      setShowRedirectScreen(false);
      setIsEditMode(false);
      setEditStandupData(null);
      setIsViewMode(false);
      setViewStandupData(null);
      setStandupId('');
      
      // If action is 'edit' and standupData is provided, trigger edit mode
      if (action === 'edit' && standupData) {
        // Small delay to ensure state is cleared before re-initializing edit mode
        setTimeout(() => {
          handleEditStandup(standupData);
        }, 0);
      }
    },
    [
      dispatch,
      isEditMode,
      standupId,
      currentLockToken,
      currentLockResourceId,
      lockReleasedRef,
      setCurrentLockToken,
      setCurrentLockResourceId,
      setShowRedirectScreen,
      setIsEditMode,
      setEditStandupData,
      setIsViewMode,
      setViewStandupData,
      setStandupId,
      fetchStandUpData,
      fetchTopOpportunitiesData,
    ],
  );

  const handleClearStandup = useCallback(() => {
    console.log('Clear standup');
  }, []);

  const handleEditStandup = useCallback(
    async (standupItem: any) => {
      // Prevent multiple simultaneous edit operations
      if (isEditProcessingRef.current) {
        console.log('[StandUp] Edit already in progress, ignoring duplicate call');
        return;
      }
      
      isEditProcessingRef.current = true;
      
      console.log('🔵 [StandUp] Edit standup clicked with item:', {
        id: standupItem?.id,
        title: standupItem?.title,
        status: standupItem?.status,
        standup_type: standupItem?.standup_type,
      });

      try {
        console.log('[StandUp] Step 1: Getting lock service...');
        let lockService = getSessionLockService();
        console.log('[StandUp] Lock service instance:', lockService ? 'EXISTS' : 'NULL');

        if (!lockService) {
          console.log('[StandUp] Step 2: Creating API client and initializing lock service...');
          const api = createApiClient();
          lockService = initializeSessionLockService(api);
          console.log('[StandUp] Lock service initialized:', lockService ? 'SUCCESS' : 'FAILED');
        }

        if (!lockService) {
          console.error('[StandUp] ❌ CRITICAL: Lock service is still null after initialization');
          return;
        }

        console.log('[StandUp] Step 3: Setting up callbacks for this lock acquisition...');
        lockService.setExpirationWarningCallback((resourceId: string, lockData: any) => {
          console.log('[StandUp] 🟡 Expiration warning callback fired for:', resourceId);
          console.log('[StandUp] 🟡 About to dispatch showLockExpirationWarning with:', {
            resourceId,
            expiresAt: lockData.expiresAt,
            lockToken: lockData.lockToken,
          });
          dispatch(
            showLockExpirationWarning({
              resourceId,
              expiresAt: lockData.expiresAt,
              lockToken: lockData.lockToken,
            }),
          );
          console.log('[StandUp] 🟡 Dispatch complete');
        });

        // Set up callback for lock released (dismiss warning popup)
        lockService.setLockReleasedCallback((resourceId: string) => {
          console.log('[StandUp] Lock released, hiding warning popup for:', resourceId);
          dispatch(hideLockExpirationWarning());
        });

        lockService.setAcquisitionFailureCallback((error: any) => {
          console.error('[StandUp] ❌ Acquisition failure callback fired:', error);
          if (error?.response?.status === 423 || error?.lockConflict) {
            const conflictData = error.lockConflict || error.response?.data;
            dispatch(
              showSessionLockConflictModal({
                isLocked: conflictData.isLocked,
                alreadyLocked: conflictData.alreadyLocked,
                lockedBy: conflictData.lockedBy,
                lockedByName: conflictData.lockedByName,
                lockedAt: conflictData.lockedAt,
                expiresAt: conflictData.expiresAt,
                resourceType: 'Standup',
              }),
            );
          }
        });

        console.log('[StandUp] Step 4: Acquiring lock with ID:', standupItem.id.toString());
        const lockResponse = await lockService.acquireLock({
          module: 'STANDUP',
          submodule: 'STANDUP',
          id: standupItem.id.toString(),
          sessionId: '',
        });

        console.log('✅ [StandUp] LOCK ACQUIRED SUCCESSFULLY:', {
          lockToken: lockResponse?.lockToken,
          expiresAt: lockResponse?.expiresAt,
          message: lockResponse?.message,
        });

        console.log('[StandUp] Step 5: Setting edit mode and state...');
        setIsEditMode(true);
        setEditStandupData(standupItem);
        setStandupType(standupItem.standup_type);
        setStandupDate(standupItem.scheduled_date);
        setStandupId(standupItem.id.toString()); // Set the actual standup ID
        setCurrentLockToken(lockResponse?.lockToken || '');
        setCurrentLockResourceId(lockResponse?.resourceId || ''); // Store full resourceId for later release
        lockReleasedRef.current = false; // Reset the flag for new lock acquisition

        // Generate session ID only once for this edit session
        // For edit mode, use the standup ID to ensure stability
        const sessionId = `Standup_Edit_${standupItem.id}_${Math.floor(Math.random() * 10000)}`;
        console.log('[StandUp] Generated stable session ID for edit:', sessionId);

        // Set session ID state immediately to prevent re-generation
        setStandupSessionId(sessionId);

        console.log('[StandUp] Step 6: Dispatching Redux session data...');
        // Store session data in Redux for editing
        dispatch(
          setSessionData({
            sessionId: sessionId, // Generated session ID (Standup_XXXXX)
            standupType: standupItem.standup_type,
            standupDate: standupItem.scheduled_date,
            standupTitle: standupItem.title,
            isEditMode: true, // Pass isEditMode flag to Redux
            standupId: standupItem.id.toString(), // Pass actual standupId from database
          }),
        );

        console.log('[StandUp] Step 7: Setting redirect screen to true...');
        setShowRedirectScreen(true);
        console.log('✅ [StandUp] ALL STEPS COMPLETED SUCCESSFULLY');
      } catch (error: any) {
        console.error('❌ [StandUp] EXCEPTION caught in handleEditStandup:', {
          message: error?.message,
          code: error?.code,
          statusCode: error?.response?.status,
          statusText: error?.response?.statusText,
          errorData: error?.response?.data,
          stack: error?.stack,
        });
      } finally {
        // Reset processing flag in all cases
        isEditProcessingRef.current = false;
      }
    },
    [
      dispatch,
      generateStandupSessionId,
      lockReleasedRef,
      setIsEditMode,
      setEditStandupData,
      setStandupType,
      setStandupDate,
      setStandupId,
      setCurrentLockToken,
      setCurrentLockResourceId,
      setStandupSessionId,
      setShowRedirectScreen,
    ],
  );

  const handleViewStandup = useCallback(
    (standupItem: any) => {
      console.log('View standup:', standupItem);

      // Set view mode and populate data
      setIsViewMode(true);
      setViewStandupData(standupItem);
      setStandupType(standupItem.standup_type);
      setStandupDate(standupItem.scheduled_date);
      setStandupId(standupItem.id.toString()); // Set the actual standup ID

      // Generate session ID for view mode (unique session identifier)
      const sessionId = generateStandupSessionId();

      // Store session data in Redux for viewing
      dispatch(
        setSessionData({
          sessionId: sessionId, // Generated session ID (Standup_XXXXX)
          standupType: standupItem.standup_type,
          standupDate: standupItem.scheduled_date,
          standupTitle: standupItem.title,
          isViewMode: true, // Pass isViewMode flag to Redux
          standupId: standupItem.id.toString(), // Pass actual standupId from database
        }),
      );

      // Set session ID state for favorites API
      setStandupSessionId(sessionId);

      // Navigate to redirect screen
      setShowRedirectScreen(true);
    },
    [
      dispatch,
      generateStandupSessionId,
      setIsViewMode,
      setViewStandupData,
      setStandupType,
      setStandupDate,
      setStandupId,
      setStandupSessionId,
      setShowRedirectScreen,
    ],
  );

  const handleViewMoreCards = useCallback(() => {
    setShowCardsScreen(true);
  }, [setShowCardsScreen]);

  const handleBackFromCards = useCallback(
    async () => {
      // Release lock if we're in edit mode and have a lock token
      // DO THIS BEFORE clearing the edit mode state
      if (isEditMode && currentLockResourceId && currentLockToken) {
        try {
          const lockService = getSessionLockService();
          if (lockService) {
            console.log('[StandUp] 🔐 Releasing lock in handleBackFromCards for resourceId:', currentLockResourceId);
            await lockService.releaseLock(currentLockResourceId, currentLockToken);
            lockReleasedRef.current = true; // Mark as released to prevent cleanup from re-releasing
            setCurrentLockToken('');
            setCurrentLockResourceId('');
          }
        } catch (error) {
          console.error('[StandUp] Error releasing lock on back from cards:', error);
        }
      }
      setShowCardsScreen(false);
    },
    [isEditMode, currentLockResourceId, currentLockToken, lockReleasedRef, setCurrentLockToken, setCurrentLockResourceId, setShowCardsScreen],
  );

  const handleOpenNewsFeedOverlay = useCallback(() => {
    setNewsFeedOverlayVisible(true);
  }, [setNewsFeedOverlayVisible]);

  const handleCloseNewsFeedOverlay = useCallback(() => {
    setNewsFeedOverlayVisible(false);
  }, [setNewsFeedOverlayVisible]);

  const handleSaveNews = useCallback(
    async (
      newsList: Array<{
        id: string;
        title: string;
        description: string;
        date: string;
        createdBy: string;
      }>,
    ) => {
      // Reload news data from API after saving
      await fetchNewsData();
    },
    [fetchNewsData],
  );

  return {
    generateStandupSessionId,
    handleOpenOverlay,
    handleCloseOverlay,
    handleContinue,
    handleBackToStandUp,
    handleClearStandup,
    handleEditStandup,
    handleViewStandup,
    handleViewMoreCards,
    handleBackFromCards,
    handleOpenNewsFeedOverlay,
    handleCloseNewsFeedOverlay,
    handleSaveNews,
  };
};
function setOverlayVisible(arg0: boolean) {
  throw new Error('Function not implemented.');
}

function setStandupType(selectedType: string) {
  throw new Error('Function not implemented.');
}

function setStandupDate(dateRange: string) {
  throw new Error('Function not implemented.');
}

function setStandupSessionId(sessionId: string) {
  throw new Error('Function not implemented.');
}

function setShowRedirectScreen(arg0: boolean) {
  throw new Error('Function not implemented.');
}

function setCurrentLockToken(arg0: string) {
  throw new Error('Function not implemented.');
}

function setCurrentLockResourceId(arg0: string) {
  throw new Error('Function not implemented.');
}

function setIsEditMode(arg0: boolean) {
  throw new Error('Function not implemented.');
}

function setEditStandupData(arg0: null) {
  throw new Error('Function not implemented.');
}

function setIsViewMode(arg0: boolean) {
  throw new Error('Function not implemented.');
}

function setViewStandupData(arg0: null) {
  throw new Error('Function not implemented.');
}

function setStandupId(arg0: string) {
  throw new Error('Function not implemented.');
}

function fetchStandUpData() {
  throw new Error('Function not implemented.');
}

function fetchTopOpportunitiesData() {
  throw new Error('Function not implemented.');
}

function setShowCardsScreen(arg0: boolean) {
  throw new Error('Function not implemented.');
}

function setNewsFeedOverlayVisible(arg0: boolean) {
  throw new Error('Function not implemented.');
}

function fetchNewsData() {
  throw new Error('Function not implemented.');
}

